import { dashboardScopePredicate } from '../../lib/portfolio/dashboardSiteScope';
import { UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT } from '../../lib/portfolio/compositeScorer';
/** Portfolio Console: four stations and responsive site cards.
 * Readiness and authorization still come from the shared portfolio spine.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { useGatedPoll } from '../../hooks/useGatedPoll';
import { siteOverviewPath } from '../../lib/siteNavigation';
import {
  COMPOSITE_STALE_EPOCH_ISO,
  computeCompositeInputsFingerprint,
} from '../../lib/portfolio/compositeScorer';
import { DECISION_STAGE_LABEL } from '../../config/reportTypes';
import { screeningScoreLabel } from '../../lib/pathToGo/stageBoards';
import { bandGlowColorVar } from '../shared/verdictVisuals';
import { useSectionViewedTelemetry } from './useSectionViewedTelemetry';
import { OrbitDetailRail } from './OrbitDetailRail';
import {
  nextStepFor,
  pipelineRank,
  useOrbitData,
  type OrbitSite,
} from './twoActDerivation';
import './twoAct.css';
import { latestSiteOutcome, portfolioStationOf, type PortfolioStation } from './portfolioConsole';
import { SiteBoundaryThumbnail, siteTotalAcres, formatSiteAcres } from '../siterag/SiteBoundaryThumbnail';
import { displayAddress } from '../../lib/siteAddressDisplay';
import { effectivePower, effectivePhases } from '../../lib/projectIntent';
import type { SiteEntity } from '../../types/site';
import { PortfolioSiteListControls, usePortfolioSiteList } from './PortfolioSiteListControls';

/** Background refresh cadence for the portfolio composites (unchanged: 30 s). */
const PORTFOLIO_COMPOSITE_POLL_MS = 30_000;

const COLUMNS: ReadonlyArray<{ id: PortfolioStation; label: string; sub: string; scope?: string }> = [
  // Stage membership (owner, 2026-09-30): environmental is Stage 2; public
  // sentiment is an additional analysis outside both stages.
  { id: 'stage1', label: DECISION_STAGE_LABEL[1], sub: 'Build the screening assessment.' },
  { id: 'interlock', label: 'Generation scenario', sub: 'Choose, solve and promote a scenario.' },
  { id: 'stage2', label: DECISION_STAGE_LABEL[2], sub: 'Validate design, cost and delivery.', scope: 'Generation & Impact · Environmental · Financial · Supply chain · Test Fit' },
  { id: 'decided', label: 'Decided', sub: 'Review the published decision.' },
];

function SiteCardIdentity({ site }: { site: SiteEntity }) {
  const siteListView = usePortfolioSiteList();
  const metadata = siteListView?.items.find(item => item.id === site.id);
  const recommendation = metadata?.recommendedCapacityLabel;
  const power = effectivePower(site.projectIntent);
  const phases = effectivePhases(site.projectIntent);
  const acres = siteTotalAcres(site);
  const location = displayAddress(site).trim();
  return <span className="pp-card-identity">
    <span className="pp-card-top">
      <span className="pp-card-name" title={site.name}>{site.name?.trim() || 'Unnamed site'}</span>
      <span className="pp-card-location">{location || 'Location not set'}</span>
      <span className="pp-card-stats">
        <span className="pp-card-mw">{power.bucket === 'undecided' && !power.isExact ? 'MW TBD' : power.displayLabel}</span>
        <span className="pp-card-acres">{acres == null ? 'Area unknown' : formatSiteAcres(acres)}</span>
        {phases.isPhased && <span>{phases.phaseCount} phases</span>}
      </span>
      {recommendation && <span className="pp-card-recommendation">{metadata?.recommendationCurrency === 'saved' ? 'Saved screening recommendation' : 'Screening recommendation'}: {recommendation}</span>}
    </span>
    <span className="pp-card-map">
      <SiteBoundaryThumbnail site={site} layout="hero" testId={`portfolio-site-boundary-${site.id}`} />
    </span>
  </span>;
}

interface DecisionPipelineBoardProps {
  customerId: string | null;
  /** Cross-light state shared with the Load Horizon + the companion rail. */
  activeSiteId?: string | null;
  onActiveSiteChange?: (siteId: string | null) => void;
  /** Left-rail site filter (search/company/verdict/scope). null ⇒ all. */
  visibleSiteIds?: ReadonlySet<string> | null;
  className?: string;
}

const SiteCard: React.FC<{
  orbitSite: OrbitSite;
  activeSiteId: string | null;
  onActiveSiteChange?: (siteId: string | null) => void;
  onPin: (siteId: string) => void;
  openSite: (siteId: string) => void;
}> = ({ orbitSite, activeSiteId, onActiveSiteChange, onPin, openSite }) => {
  const { site, verdict, stage, composite } = orbitSite;
  const id = site.id;
  const generationOutcomes = useStore(state => state.siteRag.generationOutcomes ?? {});
  const siteReports = useStore(state => state.siteRag.reports ?? []);
  const presentTypes = React.useMemo(() => [
    ...(composite.decisionStages?.screening.presentTypes ?? []),
    ...(composite.decisionStages?.validation.presentTypes ?? []),
  ], [composite.decisionStages]);
  const latestOutcome = latestSiteOutcome(Object.values(generationOutcomes), id, {
    reports: siteReports,
    presentTypes,
  });
  const latestFailedJob = latestOutcome?.status === 'failed' ? latestOutcome : undefined;
  const name = site.name ?? 'Unnamed site';
  const published = verdict.band != null;
  const crossLight = activeSiteId == null ? '' : activeSiteId === id ? ' is-active' : ' is-dim';
  const stripe = published
    ? (bandGlowColorVar(verdict.bandColor) ?? 'var(--text-muted)')
    : stage.kind === 'unassessed'
      ? 'color-mix(in srgb, var(--text-muted) 55%, transparent)'
      : 'var(--primary)';
  const next = nextStepFor(orbitSite);
  const score = composite.decisionStages?.screening.score;
  const power = effectivePower(site.projectIntent);
  const mwText = power.bucket === 'undecided' && !power.isExact ? 'MW TBD' : power.displayLabel;
  const acres = siteTotalAcres(site);
  const declinedAtScreening = published && verdict.band === 'no-go' && stage.screening?.outcome === 'declined';
  const decidedNote = !published
    ? null
    : verdict.band === 'go'
      ? 'GO reached'
      : declinedAtScreening
        ? 'declined at screening'
        : `${orbitSite.distanceToGo.toFixed(1)} pts from GO`;
  const ariaLabel = [
    name,
    displayAddress(site).trim() || 'Location not set',
    acres == null ? 'Area unknown' : formatSiteAcres(acres),
    mwText,
    published ? verdict.bandLabel : stage.label,
    `next: ${next.label}`,
    'Press Enter to open the site detail.',
  ].join(' · ');

  return (
    <button
      type="button"
      className={`pp-card${crossLight}`}
      style={{ '--c': stripe } as React.CSSProperties}
      data-testid={`pipeline-card-${id}`}
      data-site={id}
      data-stage={stage.kind}
      data-column={portfolioStationOf(orbitSite)}
      aria-label={ariaLabel}
      onClick={() => onPin(id)}
      onDoubleClick={() => openSite(id)}
      onMouseEnter={() => onActiveSiteChange?.(id)}
      onMouseLeave={() => onActiveSiteChange?.(null)}
      onFocus={() => onActiveSiteChange?.(id)}
      onBlur={() => onActiveSiteChange?.(null)}
    >
      <SiteCardIdentity site={site} />

      {/* No status/reason chip on the card (owner, 2026-09-30): the reason is
          in the rail and the next step below. Only a failed analysis job, which
          nothing else on the card or rail reports, stays — as plain muted text. */}
      {!published && latestFailedJob ? (
        <span className="pp-row">
          <span className="pp-note" data-testid={`pipeline-card-failure-${id}`}>
            {latestFailedJob.errorCode === 'PROVIDER_COVERAGE_BLOCKED'
              ? 'Analysis blocked: missing provider coverage'
              : latestFailedJob.errorCode === 'CITATION_DENSITY_BLOCKED'
                ? 'Analysis blocked: low citation density'
                : latestFailedJob.error || 'Analysis failed to compile'}
          </span>
        </span>
      ) : null}

      {stage.kind === 'validating' && stage.screening && score != null ? (
        <span className="pp-row">
          <span className="pp-note" data-testid={`pipeline-screening-score-${id}`}>
            {screeningScoreLabel(score, undefined, 'saved')}
          </span>
        </span>
      ) : null}

      {published ? (
        <span className="pp-band">
          <b style={{ color: stripe }}>{verdict.bandLabel}</b>
          {decidedNote ? <span>{decidedNote}</span> : null}
        </span>
      ) : null}

      <span className="pp-next">Next · <b>{next.label}</b></span>
    </button>
  );
};

export function DecisionPipelineBoard({
  customerId,
  activeSiteId = null,
  onActiveSiteChange,
  visibleSiteIds = null,
  className = '',
}: DecisionPipelineBoardProps) {
  const sectionRef = useSectionViewedTelemetry('decision-pipeline');
  const siteListView = usePortfolioSiteList();
  const { sites, notComputedCount } = useOrbitData(customerId, visibleSiteIds);
  const setActiveSite = useStore((s) => s.setActiveSite);
  const computing = useStore((s) => s.portfolio.computing);
  const reportsLoaded = useStore((s) => s.siteRag.isReportsLoaded);
  const reports = useStore((s) => s.siteRag.reports);
  const analysisContexts = useStore((s) => s.portfolio.analysisContexts);
  const allSites = useStore((s) => s.siteRag.sites);
  const allComposites = useStore((s) => s.portfolio.composites);
  const isAdmin = useStore((s) => s.userRole === 'admin');
  const subtreeCompanies = useStore((s) => s.subtreeCompanies);
  const ensurePortfolioComposites = useStore((s) => s.ensurePortfolioComposites);
  const navigate = useNavigate();
  // Each pass re-reads `GET /api/decision-analyses/:siteId` for EVERY in-scope
  // site, so the poll pauses in a hidden tab (one catch-up pass on return if a
  // tick was missed). Overlap is already refused by the store's own
  // `portfolio.computing` guard inside ensurePortfolioComposites.
  useGatedPoll({
    active: Boolean(customerId) && reportsLoaded,
    intervalMs: PORTFOLIO_COMPOSITE_POLL_MS,
    onFetch: () => { if (customerId) void ensurePortfolioComposites(customerId); },
  });
  // A current fingerprint VERSION can still describe old inputs. React to
  // report hydration, intent/SED/scenario edits and explicit invalidation even
  // when the number of sites stays the same. Use a scalar key so hover and
  // equivalent persisted-composite reloads do not trigger another read.
  const compositeRefreshKey = React.useMemo(() => {
    const inScope = dashboardScopePredicate(customerId, { isAdmin, subtreeCompanies });
    return JSON.stringify(allSites.filter(site => inScope(site)
      && (visibleSiteIds == null || visibleSiteIds.has(site.id))).map(site => [
      site.id,
      computeCompositeInputsFingerprint({ site, reports, analysisContext: analysisContexts?.[site.id] ?? UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT }),
      allComposites[site.id]?.inputsFingerprint,
      allComposites[site.id]?.lastComputedAt === COMPOSITE_STALE_EPOCH_ISO,
    ]).sort(([a], [b]) => String(a).localeCompare(String(b))));
  }, [allSites, allComposites, reports, analysisContexts, customerId, isAdmin, subtreeCompanies, visibleSiteIds]);
  const requestedRefreshKey = React.useRef<string | null>(null);

  // Columns: one per pipeline state, cards sorted by closeness to leaving.
  const byColumn = React.useMemo(() => {
    const groups: Record<PortfolioStation, OrbitSite[]> = { stage1: [], interlock: [], stage2: [], decided: [] };
    for (const orbitSite of sites) groups[portfolioStationOf(orbitSite)].push(orbitSite);
    const selectedOrder = new Map(siteListView?.items.map((item, index) => [item.id, index]));
    for (const id of Object.keys(groups) as PortfolioStation[]) {
      groups[id].sort((a, b) => siteListView
        ? (selectedOrder.get(a.site.id) ?? 0) - (selectedOrder.get(b.site.id) ?? 0)
        : pipelineRank(a) - pipelineRank(b));
    }
    return groups;
  }, [sites, siteListView]);
  const decided = byColumn.decided.map((s) => s.verdict.band);
  const decidedSummary = byColumn.decided.length > 0
    ? `${decided.filter((b) => b === 'go').length} proceed · ${decided.filter((b) => b === 'conditional' || b === 'conditional-high').length} conditional · ${decided.filter((b) => b === 'no-go').length} declined`
    : null;

  // The rail "pins" a site on click (distinct from the transient hover/focus
  // cross-light). Collapsed by default so the board gets the full width.
  const [pinnedSiteId, setPinnedSiteId] = React.useState<string | null>(null);
  const [railOpen, setRailOpen] = React.useState(false);
  const railSite = React.useMemo<OrbitSite | null>(() => {
    if (sites.length === 0) return null;
    const byPin = pinnedSiteId ? sites.find((s) => s.site.id === pinnedSiteId) : undefined;
    const byHover = activeSiteId ? sites.find((s) => s.site.id === activeSiteId) : undefined;
    return byPin ?? byHover ?? sites[0];
  }, [sites, pinnedSiteId, activeSiteId]);

  // Auto-populate on mount: opening the dashboard computes any missing/stale
  // composites (idempotent; the store's fingerprint-aware guard decides).
  React.useEffect(() => {
    if (!customerId || !reportsLoaded || computing || sites.length + notComputedCount === 0) return;
    const requestKey = `${customerId}:${compositeRefreshKey}`;
    if (requestedRefreshKey.current === requestKey) return;
    requestedRefreshKey.current = requestKey;
    void ensurePortfolioComposites?.(customerId);
  }, [
    customerId,
    reportsLoaded,
    computing,
    sites.length,
    notComputedCount,
    compositeRefreshKey,
    ensurePortfolioComposites,
  ]);

  const openSite = React.useCallback(
    (siteId: string) => {
      setActiveSite(siteId);
      navigate(siteOverviewPath(siteId));
    },
    [setActiveSite, navigate],
  );
  // Resolve → the site tab that owns the rail's next step (the tab the button
  // names — OrbitDetailRail resolves it, rolled-back decision included). It
  // used to open the retired Path to GO tab.
  const resolveNextStep = React.useCallback(
    (siteId: string, tab: string) => {
      setActiveSite(siteId);
      navigate(siteOverviewPath(siteId, tab));
    },
    [setActiveSite, navigate],
  );
  const pinSite = React.useCallback(
    (siteId: string) => {
      setPinnedSiteId(siteId);
      setRailOpen(true);
      onActiveSiteChange?.(siteId);
    },
    [onActiveSiteChange],
  );

  const [stationFilter, setStationFilter] = React.useState<PortfolioStation | null>(null);
  const selectedStation = siteListView ? siteListView.state.decisionStage : stationFilter;
  const awaiting = React.useMemo(() => {
    const inScope = dashboardScopePredicate(customerId, { isAdmin, subtreeCompanies });
    const placed = new Set(sites.map(item => item.site.id));
    return allSites.filter(site => inScope(site) && (visibleSiteIds == null || visibleSiteIds.has(site.id)) && !placed.has(site.id));
  }, [allSites, sites, customerId, isAdmin, subtreeCompanies, visibleSiteIds]);

  return (
    <div className={`pp-split portfolio-console ${className}`.trim()} data-rail={railOpen ? 'open' : 'closed'} data-testid="decision-pipeline-split">
      <section ref={sectionRef} className="instrument-panel portfolio-panel min-w-0" aria-label="Portfolio path to decision" data-testid="decision-pipeline">
        <header className="instrument-section-head"><div><h2 className="type-section text-text-primary">Portfolio Console</h2><p className="type-meta text-text-muted" data-testid="decision-pipeline-status">{sites.length} placed · {awaiting.length} awaiting compute</p></div><button type="button" className="type-meta text-text-secondary underline" onClick={() => { requestedRefreshKey.current = null; if (customerId) void ensurePortfolioComposites?.(customerId); }} disabled={computing || !customerId} aria-label="Refresh portfolio">{computing ? 'Computing…' : 'Refresh'}</button></header>
        <PortfolioSiteListControls />
        <ol className="instrument-path portfolio-stations" aria-label="Portfolio stations">
          {COLUMNS.map(column => <li key={column.id} data-testid={`pipeline-column-${column.id}`}>
            <button type="button" aria-pressed={selectedStation === column.id} aria-description={column.scope} className="portfolio-station-button" onClick={() => {
              if (siteListView) siteListView.onChange(current => ({ ...current, decisionStage: current.decisionStage === column.id ? 'all' : column.id }));
              else setStationFilter(current => current === column.id ? null : column.id);
            }}>
              <span className="portfolio-station-label text-text-secondary">{column.label}</span>
              <strong className="portfolio-station-count text-text-primary" data-testid={`pipeline-count-${column.id}`}>{byColumn[column.id].length}</strong>
              <small className="portfolio-station-description text-text-muted">{column.id === 'decided' && decidedSummary ? decidedSummary : column.sub}</small>
            </button>
          </li>)}
        </ol>
        <div className="portfolio-site-groups">
          {siteListView ? <section aria-label="Sites" data-testid="portfolio-filtered-sites">
            <div className="portfolio-site-grid">
              {siteListView.items.map(item => {
                const orbitSite = sites.find(placed => placed.site.id === item.id);
                if (orbitSite) return <SiteCard key={item.id} orbitSite={orbitSite} activeSiteId={pinnedSiteId ?? activeSiteId} onActiveSiteChange={onActiveSiteChange} onPin={pinSite} openSite={openSite} />;
                if (!awaiting.some(site => site.id === item.id)) return null;
                return <button type="button" key={item.id} className="pp-card portfolio-awaiting-card" onClick={() => openSite(item.id)} data-testid={`portfolio-awaiting-${item.id}`}>
                  <SiteCardIdentity site={item.site} />
                  <span className="pp-note">{computing ? 'Computing current inputs…' : 'Queued for compute'}</span>
                  <span className="pp-next"><b>Open site →</b></span>
                </button>;
              })}
            </div>
            {sites.length + awaiting.length === 0 && <div className="portfolio-view-empty" data-testid="decision-pipeline-filter-empty"><p>No sites match these filters.</p><button type="button" onClick={siteListView.onReset}>Clear search and filters</button></div>}
          </section> : <>
          {COLUMNS.filter(column => stationFilter == null || stationFilter === column.id).map(column => <section key={column.id} aria-label={column.label} data-testid={`portfolio-sites-${column.id}`}>
            <h3 className="type-meta text-text-muted portfolio-group-title">{column.label} · {byColumn[column.id].length}</h3>
            <div className="portfolio-site-grid">
              {byColumn[column.id].map(orbitSite => <SiteCard key={orbitSite.site.id} orbitSite={orbitSite} activeSiteId={pinnedSiteId ?? activeSiteId} onActiveSiteChange={onActiveSiteChange} onPin={pinSite} openSite={openSite} />)}
            </div>
            {byColumn[column.id].length === 0 && <p className="type-meta text-text-muted py-3" data-testid={`pipeline-empty-${column.id}`}>No sites here</p>}
          </section>)}
          {awaiting.length > 0 && <section aria-label="Awaiting compute" data-testid="portfolio-awaiting-compute">
            <h3 className="type-meta text-text-muted portfolio-group-title">Awaiting compute · {awaiting.length}</h3>
            <div className="portfolio-site-grid">
              {awaiting.map(site => <button type="button" key={site.id} className="pp-card portfolio-awaiting-card" onClick={() => openSite(site.id)} data-testid={`portfolio-awaiting-${site.id}`}>
                <SiteCardIdentity site={site} />
                <span className="pp-note">{computing ? 'Computing current inputs…' : 'Queued for compute'}</span>
                <span className="pp-next"><b>Open site →</b></span>
              </button>)}
            </div>
          </section>}
          {sites.length + awaiting.length === 0 && <p className="type-body text-text-secondary py-8" data-testid="decision-pipeline-empty">Add a site to begin early screening.</p>}
          </>}
        </div>
        {railSite && <button type="button" className="type-meta text-text-secondary underline mt-4" onClick={() => setRailOpen(value => !value)} data-testid="pipeline-rail-reopen">{railOpen ? 'Close site detail' : 'Show site detail'}</button>}
      </section>
      <OrbitDetailRail site={railSite} onResolve={resolveNextStep} onOpenSite={openSite} onClose={() => setRailOpen(false)} />
    </div>
  );
}

export default DecisionPipelineBoard;
